import { Button } from "@/components/ui/Button";
import { Check } from "@/components/ui/icons";
import { DEMO_SUCCESS, LINKS } from "@/content/quikit";
import { cn } from "@/lib/utils";

/**
 * What the sender sees once the demo form is in: the same panel whether it arrives as the
 * dialog over the form or as the standalone /form-submitted page, so the two cannot drift.
 *
 * The heading level differs between the two, though: over the form it is a section of a page
 * that already has an h1, while on /form-submitted it IS the page, so `as` lets the caller say
 * which. It stays the dialog's h2 by default.
 */
export function DemoSuccessPanel({
  name,
  className,
  as: Heading = "h2",
}: {
  name?: string;
  className?: string;
  as?: "h1" | "h2";
}) {
  const first = name?.trim().split(/\s+/)[0];

  return (
    <div className={className}>
      <span className="inline-flex items-center gap-2 rounded-full bg-accent-soft px-3 py-1 text-[12px] font-medium text-accent">
        <span className="size-1.5 rounded-full bg-mint" />
        {DEMO_SUCCESS.pill}
      </span>

      <Heading id="demo-success-title" className="mt-5 text-[clamp(1.6rem,3vw,2.1rem)] font-[450] tracking-[-0.035em]">
        {DEMO_SUCCESS.greeting}
        {first ? `, ${first}` : ""}.
      </Heading>
      <p className="mt-3 max-w-[42ch] text-[14.5px] leading-[1.6] text-muted">{DEMO_SUCCESS.text}</p>

      <hr className="my-7 border-line" />

      <ul className="space-y-3.5">
        {DEMO_SUCCESS.points.map((p) => (
          <li key={p} className="flex items-start gap-3 text-[14px] leading-[1.55]">
            <span className="mt-[2px] grid size-[20px] shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
              <Check className="size-3" strokeWidth={2.6} />
            </span>
            {p}
          </li>
        ))}
      </ul>

      <div className={cn("mt-8 flex flex-wrap items-center gap-x-6 gap-y-3")}>
        <Button href="/" variant="mint">
          Back to home
        </Button>
        <a href={LINKS.register} className="text-[14px] font-medium underline underline-offset-4 hover:text-accent">
          Start free
        </a>
      </div>
    </div>
  );
}
